import { useEffect, useState } from "react";
import { RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { api, timeAgo, formatApiError } from "../lib/api";
import Pager from "./Pager";

export function CoursesAdmin() {
  const [cfg, setCfg] = useState(null);
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [page, setPage] = useState(1);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadCfg = () => api.get("/admin/courses/settings").then((r) => setCfg(r.data)).catch(() => toast.error("Couldn't load course settings"));
  const load = (p = page, query = q, act = activeFilter) =>
    api.get("/admin/courses", { params: { page: p, q: query, active: act } })
      .then((r) => setList(r.data)).catch(() => toast.error("Couldn't load courses"));

  useEffect(() => { loadCfg(); load(1, "", ""); }, []);

  const toggleFeature = async () => {
    setSaving(true);
    try {
      await api.put("/admin/courses/settings", { enabled: !cfg.enabled });
      toast.success(!cfg.enabled ? "Courses & Skills is live on the site" : "Courses & Skills hidden from the site");
      loadCfg();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/courses/sync");
      if (r.data.ok) toast.success(`Sync done, ${r.data.added} new, ${r.data.updated} refreshed`);
      else toast.error(r.data.error || "Sync failed");
      loadCfg(); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSyncing(false); }
  };

  const toggleCourse = async (c) => {
    try {
      await api.patch(`/admin/courses/${c.id}`, { active: !c.active });
      toast.success(c.active ? "Course hidden" : "Course restored");
      load(); loadCfg();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!cfg) return <div className="h-48 rounded-md bg-zinc-50 animate-pulse" data-testid="courses-admin-loading" />;

  return (
    <div className="space-y-5" data-testid="courses-admin">
      <div className="bg-white border border-zinc-200 rounded-lg p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
          <h3 className="font-display text-base font-bold text-zinc-950">Courses & Skills</h3>
          <label className="flex items-center gap-2 text-xs font-semibold text-zinc-600 cursor-pointer"
            title="When on, /courses is public and job pages show matched courses">
            <input type="checkbox" checked={cfg.enabled} onChange={toggleFeature} disabled={saving}
              data-testid="courses-enabled" className="accent-zinc-900" />
            Live on site
          </label>
        </div>
        <p className="text-xs text-zinc-500 max-w-2xl leading-relaxed">
          Real UK courses from the National Careers Service Course Directory, matched to your job categories —
          shown on /courses and beside every live job. Data © Crown copyright, attribution shown on the page.
        </p>
        <div className="flex items-center gap-4 mt-4 flex-wrap">
          <button onClick={sync} disabled={syncing || !cfg.ncs_key_configured} data-testid="courses-sync"
            title={cfg.ncs_key_configured ? "Pull the latest courses now" : "Add NCS_API_KEY to the backend environment first"}
            className="h-9 px-4 inline-flex items-center gap-2 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50 transition-colors duration-200">
            <RefreshCw size={13} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing…" : "Sync now"}
          </button>
          <span className={`text-[11px] font-semibold px-2 py-1 rounded ${cfg.ncs_key_configured ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}
            data-testid="courses-key-status">
            {cfg.ncs_key_configured ? "NCS API key configured" : "NCS API key missing, register free at portal.api.nationalcareers.service.gov.uk"}
          </span>
          {cfg.last_sync && (
            <span className="text-[11px] text-zinc-400" data-testid="courses-last-sync">
              Last sync {timeAgo(cfg.last_sync.at)} · {cfg.last_sync.status}
              {cfg.last_sync.status === "ok" && ` · +${cfg.last_sync.added} new`}
              {cfg.last_sync.error && ` · ${cfg.last_sync.error.slice(0, 60)}`}
            </span>
          )}
        </div>
        <p className="text-xs text-zinc-500 mt-3" data-testid="courses-counts">
          <strong className="text-zinc-900">{cfg.total}</strong> live courses · {cfg.hidden} hidden · weekly automatic sync every Tuesday
        </p>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="font-display text-base font-bold text-zinc-950">Course catalogue</h3>
          <div className="flex gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (setPage(1), load(1))}
              placeholder="Search title or provider…" data-testid="courses-admin-search"
              className="h-9 w-56 px-3 text-sm border border-zinc-300 rounded-md focus:outline-none focus:border-zinc-900" />
            <select value={activeFilter} onChange={(e) => { setActiveFilter(e.target.value); setPage(1); load(1, q, e.target.value); }}
              data-testid="courses-admin-filter"
              className="h-9 px-2 text-xs border border-zinc-300 rounded-md bg-white">
              <option value="">All</option><option value="1">Live</option><option value="0">Hidden</option>
            </select>
            <button onClick={() => { setPage(1); load(1); }} data-testid="courses-admin-search-btn" aria-label="Search"
              className="h-9 px-3 border border-zinc-300 rounded-md text-zinc-600 hover:border-zinc-900 transition-colors duration-200">
              <Search size={15} />
            </button>
          </div>
        </div>
        {!list || list.items.length === 0 ? (
          <p className="text-sm text-zinc-400" data-testid="courses-admin-empty">
            {cfg.ncs_key_configured ? "No courses yet, press Sync now." : "Add the NCS API key, then press Sync now to fill the catalogue with real courses."}
          </p>
        ) : (
          <>
            <div className="space-y-1.5 max-h-96 overflow-y-auto">
              {list.items.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 border border-zinc-100 rounded-md px-3 py-2"
                  data-testid={`courses-admin-row-${c.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-zinc-900 truncate">{c.title}</p>
                    <p className="text-xs text-zinc-500 truncate">{c.provider} · {c.category}{c.online ? " · Online" : ""}</p>
                  </div>
                  <button onClick={() => toggleCourse(c)} data-testid={`courses-admin-toggle-${c.id}`}
                    title={c.active ? "Hide from the public catalogue" : "Show in the public catalogue"}
                    className={`shrink-0 h-7 px-3 text-[11px] font-semibold rounded-full transition-colors duration-200 ${c.active ? "bg-green-50 text-green-700 hover:bg-green-100" : "bg-zinc-100 text-zinc-500 hover:bg-zinc-200"}`}>
                    {c.active ? "Live" : "Hidden"}
                  </button>
                </div>
              ))}
            </div>
            <Pager page={page} pages={list.pages} onPage={(p) => { setPage(p); load(p); }} />
          </>
        )}
      </div>
    </div>
  );
}
